import React, { useState, useEffect } from 'react';
import api from '../../api/client';
import { Save, Plus, Trash2, AlertCircle, CheckCircle } from 'lucide-react';

function Notification({ message, type, onClose }) {
    useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t); }, [onClose]);
    const colors = type === 'success' ? 'bg-green-50 border-green-400 text-green-800' : 'bg-red-50 border-red-400 text-red-800';
    return (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg border-l-4 shadow-lg ${colors} flex items-center gap-2`}>
            {type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
            {message}
        </div>
    );
}

export default function ProfilePage() {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [notif, setNotif] = useState(null);

    // Form state
    const [form, setForm] = useState({
        full_name: '', title: '', bio: '', location: '',
        email: '', phone: '', linkedin_url: '', github_url: '', portfolio_url: '',
        skills: '', experience: '', education: '', languages_spoken: '',
    });

    useEffect(() => {
        api.get('/profile').then(({ data }) => {
            setProfile(data);
            // Convert JSONB objects to readable text for free-form editing
            const toText = (val) => {
                if (val === null || val === undefined) return '';
                if (typeof val === 'string') return val;
                return JSON.stringify(val, null, 2);
            };
            setForm({
                ...data,
                skills: toText(data.skills),
                experience: toText(data.experience),
                education: toText(data.education),
                languages_spoken: toText(data.languages_spoken),
            });
        }).finally(() => setLoading(false));
    }, []);

    const handleChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            // Try to parse as JSON, otherwise wrap as plain text for the backend
            const parseField = (val) => {
                if (!val || val.trim() === '') return null;
                try {
                    return JSON.parse(val);
                } catch {
                    // Not JSON — send as plain text string, backend will store as-is
                    return val;
                }
            };
            const payload = {
                full_name: form.full_name,
                title: form.title,
                bio: form.bio,
                location: form.location,
                email: form.email,
                phone: form.phone,
                linkedin_url: form.linkedin_url,
                github_url: form.github_url,
                portfolio_url: form.portfolio_url,
                skills: parseField(form.skills),
                experience: parseField(form.experience),
                education: parseField(form.education),
                languages_spoken: parseField(form.languages_spoken),
            };
            const { data } = await api.put('/profile', payload);
            setProfile(data);
            setNotif({ type: 'success', message: 'Profil mis à jour !' });
        } catch (err) {
            setNotif({ type: 'error', message: err.response?.data?.detail || 'Erreur de sauvegarde' });
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="text-gray-500">Chargement...</div>;

    return (
        <div>
            {notif && <Notification {...notif} onClose={() => setNotif(null)} />}

            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Mon Profil</h1>
                <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 bg-[#5764F4] hover:bg-[#4654E3] text-white px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50">
                    <Save className="w-4 h-4" />
                    {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                </button>
            </div>

            <div className="space-y-6">
                {/* Basic info */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Informations générales</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {[
                            { label: 'Nom complet', field: 'full_name', placeholder: 'Abdoulaye Diallo' },
                            { label: 'Titre', field: 'title', placeholder: 'Développeur Full Stack' },
                            { label: 'Localisation', field: 'location', placeholder: 'Paris, France' },
                            { label: 'Email', field: 'email', type: 'email', placeholder: 'contact@email.com' },
                            { label: 'Téléphone', field: 'phone', placeholder: '+33 6 XX XX XX XX' },
                            { label: 'LinkedIn', field: 'linkedin_url', placeholder: 'https://linkedin.com/in/...' },
                            { label: 'GitHub', field: 'github_url', placeholder: 'https://github.com/...' },
                            { label: 'Portfolio', field: 'portfolio_url', placeholder: 'https://...' },
                        ].map(({ label, field, type, placeholder }) => (
                            <div key={field}>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                                <input
                                    type={type || 'text'}
                                    value={form[field] || ''}
                                    onChange={(e) => handleChange(field, e.target.value)}
                                    placeholder={placeholder}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5764F4] focus:border-transparent text-sm"
                                />
                            </div>
                        ))}
                    </div>
                    <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                        <textarea
                            value={form.bio || ''}
                            onChange={(e) => handleChange('bio', e.target.value)}
                            rows={4}
                            placeholder="Décrivez-vous en quelques lignes..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5764F4] focus:border-transparent text-sm"
                        />
                    </div>
                </div>

                {/* Rich text fields — enter information freely */}
                {[
                    { label: 'Compétences', field: 'skills', placeholder: 'Python, JavaScript, React, FastAPI, Docker, PostgreSQL, Git\nOu tout autre format qui vous convient...' },
                    { label: 'Expérience professionnelle', field: 'experience', placeholder: 'Développeur Full Stack chez TechCorp (2021-2024)\n- Création d\'une plateforme SaaS multi-tenant\n- Stack: Python, FastAPI, React, PostgreSQL\n\nDéveloppeur Backend chez StartupXYZ (2019-2021)\n- API REST, microservices...' },
                    { label: 'Formation', field: 'education', placeholder: 'Master Informatique - Université de Paris (2019)\nLicence Informatique - Université de Lyon (2017)\nCertification AWS Solutions Architect...' },
                    { label: 'Langues', field: 'languages_spoken', placeholder: 'Français (natif)\nAnglais (courant)\nEspagnol (intermédiaire)' },
                ].map(({ label, field, placeholder }) => (
                    <div key={field} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-2">{label}</h2>
                        <p className="text-xs text-gray-400 mb-3">Entrez vos informations librement, l'IA les intégrera automatiquement.</p>
                        <textarea
                            value={form[field] || ''}
                            onChange={(e) => handleChange(field, e.target.value)}
                            rows={6}
                            placeholder={placeholder}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5764F4] focus:border-transparent text-sm"
                        />
                    </div>
                ))}
            </div>
        </div>
    );
}
